<template>
  <article
    v-if="post && Object.keys(post).length"
    :class="[isMobile() ? 'space-0-above' : { grid: !isTablet() }, 'container']"
  >
    <figure :class="[$style.video, 'aspect-container']" v-html="post.embed"></figure>
    <header>
      <nav>
        <BackLink component="VideoPost" group="video" label="Video" name="Video"></BackLink>
      </nav>
      <h1 v-html="post.title"></h1>
      <time
        :class="[$style.time, 'subtitle-2', 'space-0-above']"
        :datetime="formatTimeDuration(post.duration)"
        v-html="post.duration"
      ></time>
      <time class="subtitle-2" :datetime="post.date" pubdate>{{ post.date }}</time>
    </header>
    <ProcessContent
      v-if="post.content"
      ref="mainContent"
      :content="post.content"
      wrapper="section"
    ></ProcessContent>
    <section v-if="post.pdf || post.transcript" :class="[$style.transcript, 'space-48-64-above']">
      <h2 v-if="post.transcript">Transcript</h2>
      <p v-if="post.transcript" v-html="post.transcript"></p>
      <a v-if="post.pdf" :href="post.pdf">Download Transcript (PDF)</a>
    </section>
    <p v-if="post.tags && post.tags.nodes.length" class="info">Tags:</p>
    <ul v-if="post.tags && post.tags.nodes.length" class="tags">
      <li v-for="(tag, index) in post.tags.nodes" :key="index">
        <router-link
          :to="{ name: 'VideoTaxonomy', params: { type: 'tag', slug: tag.slug } }"
          :class="{ 'tag-spacing': index < post.tags.nodes.length - 1 }"
        >
          {{ tag.name }}
        </router-link>
      </li>
    </ul>
    <p v-if="post.categories && post.categories.nodes.length" class="info">Categories:</p>
    <ul v-if="post.categories && post.categories.nodes.length" class="tags">
      <li
        v-for="(category, index) in post.categories.nodes"
        :key="index"
        :class="$style.taxonomyItem"
      >
        <router-link
          :class="{ 'tag-spacing': index < post.categories.nodes.length - 1 }"
          :to="{ name: 'VideoTaxonomy', params: { type: 'category', slug: category.slug } }"
        >
          {{ category.name }}
        </router-link>
      </li>
    </ul>
  </article>
</template>

<script setup lang="ts">
import BackLink from '@/components/common/BackLink.vue';
import { dataLoaded, useHandleError, useMetaData, useScreenSize } from '@/composables/Common.js';
import { formatTimeDuration } from '@/utils.js';
import ProcessContent from '@/components/common/ProcessContent.vue';
import { ref } from 'vue';
import { useQuery } from 'villus';
import { Video } from '@/types/video.interface';
import { videoPostQuery } from '@/queries/GetVideoPost';

const props = defineProps<{
  postSlug: string;
}>();

const { handleError } = useHandleError();
const { isMobile, isTablet } = useScreenSize();
const { setMetaDescription, setMetaTitle } = useMetaData();

const post = ref<Video>({});

const { data } = useQuery({
  onData: (data) => {
    if (data !== undefined && data.video && Object.keys(data.video).length) {
      post.value = data.video;
      setMetaTitle(data.video.title);
      setMetaDescription(data.video.excerpt);
      dataLoaded();
    }

    if (!data || !data.video || Object.keys(data.video).length === 0) {
      handleError('VideoPost', props.postSlug);
    }
  },
  query: videoPostQuery,
  variables: { slug: props.postSlug },
});
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: videoPostQuery,
          variables: {
            slug: params.postSlug,
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss" module>
.time {
  display: inline-block;

  &::after {
    content: '|';
    margin: 0 0.25rem;
  }
}

.transcript {
  a[href$='.pdf'][href$='.pdf'] {
    margin-bottom: 0;
    margin-top: 1rem;
  }
}

.video {
  @include space-0-above;

  iframe {
    height: 100%;
    position: absolute;
    width: 100%;
  }
}

@media (min-width: $break-point) {
  article {
    > * {
      grid-column: 1 / span 8;
    }

    nav,
    .video {
      grid-column: span 12;
    }
  }
}
</style>
